import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import type {
  ProductAppliedFilters,
  ProductFilterDescriptor,
} from '~/features/explorer/types/products-catalog.types';

/** One removable active-filter chip: the facet key + (for list facets) the value. */
interface FilterChip {
  key: string;
  value: string | null;
  label: string;
}

interface ProductActiveFiltersProps {
  descriptors: ProductFilterDescriptor[];
  applied: ProductAppliedFilters;
  onChange: (next: ProductAppliedFilters) => void;
}

/** Removes a key from the applied map without a dynamic `delete`. */
function omitKey(applied: ProductAppliedFilters, key: string): ProductAppliedFilters {
  return Object.fromEntries(Object.entries(applied).filter(([k]) => k !== key));
}

/** Flattens the applied-filter map into one chip per selected value. */
function toChips(applied: ProductAppliedFilters, labelByKey: Map<string, string>): FilterChip[] {
  const chips: FilterChip[] = [];
  for (const [key, val] of Object.entries(applied)) {
    const facet = labelByKey.get(key) ?? key;
    if (Array.isArray(val)) {
      for (const v of val) chips.push({ key, value: v, label: `${facet}: ${v}` });
    } else if (val === true) {
      chips.push({ key, value: null, label: facet });
    } else if (typeof val === 'string' && val) {
      chips.push({ key, value: val, label: `${facet}: ${val}` });
    }
  }
  return chips;
}

/**
 * Active-filter "batch" bar — one removable chip per selected facet value on its
 * own line, plus a Clear all. Removing a chip drops just that value (or the whole
 * key for boolean / single-value facets) from the applied filter map.
 */
export function ProductActiveFilters({
  descriptors,
  applied,
  onChange,
}: ProductActiveFiltersProps) {
  const { t } = useTranslation('explorer');
  const labelByKey = new Map(descriptors.map((d) => [d.key, d.label]));
  const chips = toChips(applied, labelByKey);

  if (chips.length === 0) return null;

  function removeChip(chip: FilterChip) {
    // Boolean / single-value facet → drop the whole key.
    if (chip.value === null) {
      onChange(omitKey(applied, chip.key));
      return;
    }
    const current = applied[chip.key];
    if (Array.isArray(current)) {
      const next = current.filter((v) => v !== chip.value);
      onChange(next.length > 0 ? { ...applied, [chip.key]: next } : omitKey(applied, chip.key));
    } else {
      onChange(omitKey(applied, chip.key));
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-muted-foreground text-xs font-medium">{t('products.activeLabel')}</span>
      {chips.map((chip) => (
        <span
          key={`${chip.key}:${chip.value ?? ''}`}
          className="bg-selected-row text-foreground inline-flex items-center gap-1 rounded-full py-1 pr-1 pl-2.5 text-xs font-medium capitalize"
        >
          {chip.label}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => {
              removeChip(chip);
            }}
            aria-label={t('products.removeFilter', { filter: chip.label })}
            className="hover:bg-muted-foreground/20 h-4 w-4 shrink-0 cursor-pointer rounded-full p-0"
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </Button>
        </span>
      ))}
      <Button
        type="button"
        variant="link"
        onClick={() => {
          onChange({});
        }}
        className="text-primary h-auto cursor-pointer p-0 text-xs font-medium"
      >
        {t('products.clearAll')}
      </Button>
    </div>
  );
}
